import React from 'react';
import { observer } from '../../../shared/observer';
import { debounce } from '../../../shared/debounce';
import { runBusy, isBusy } from '../../../shared/busy';
import { curFormat } from '../../../shared/format';
import { router } from '../../../shared/hashRouter';
import { PayType, isOnlinePayment, isCardPayment } from './PayType';
import { CheckoutSteps } from './CheckoutSteps';

// Шаг 2 — оплата. Аналог мобильного `client_app/.../Cart/OrderForm/index.tsx`.
// Summary, промокод, бонусы, способ оплаты, submit. При маунте проверяет,
// что заказ был сконвертирован на шаге 1; иначе редиректит на `#checkout`.

const PromoIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20.59 13.41 13.41 20.59a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82Z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);

class PaymentFormView extends React.Component {
  state = {
    submitting: false,
    error: null,
    promoModalOpen: false,
    promoInput: '',
    promoError: null,
    applyingPromo: false,
    busy: {},
    // null — клиент галку не трогал, берём checked_default из настроек компании
    saveCard: null,
    submittingJustPay: false,
  };

  componentWillMount() {
    // Фоновый автосейв: ошибку глотаем — submit всё равно шлёт полный
    // fieldsToUpdateOrder, так что потерянный автосейв восстановится.
    this.pushUpdate = debounce(() => this.props.order.updateCommonParams().catch(() => {}), 500);
  }

  componentDidMount() {
    const order = this.props.order.order || {};
    if (!order.id) {
      router.go('#checkout');
      return;
    }
    // Deep-link мимо шага подписи: акт ещё не подписан — возвращаем на него,
    // иначе клиент упрётся в 400 от гейта уже на кнопке оплаты
    if (this.props.order.rentActSignRequired) {
      router.go('#tare_sign');
    }
  }

  goBack = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (this.state.submitting) return;
    router.go('#checkout');
  };

  openPromo = () => {
    if (this.state.submitting) return;
    this.setState({ promoModalOpen: true, promoInput: '', promoError: null });
  };

  closePromo = () => {
    // Пока промокод применяется — диалог закрыть нельзя (ответ придёт «в никуда»).
    if (this.state.applyingPromo) return;
    this.setState({ promoModalOpen: false, promoError: null });
  };

  setPromoInput = (e) => this.setState({ promoInput: e.target.value, promoError: null });

  applyPromo = async () => {
    const code = (this.state.promoInput || '').trim();
    if (!code) return;
    this.setState({ applyingPromo: true, promoError: null });
    try {
      this.pushUpdate.cancel();
      this.props.order.setFieldsToUpdateOrder({ promo_code_text: code });
      const res = await this.props.order.applyPromoCode();
      if (res && res.errors && res.errors.promo_code_text) {
        this.setState({ promoError: 'Промокод не применился', applyingPromo: false });
      } else {
        this.setState({ promoModalOpen: false, applyingPromo: false, promoInput: '' });
      }
    } catch (err) {
      this.setState({ applyingPromo: false, promoError: err.message || 'Ошибка' });
    }
  };

  cancelPromo = () => {
    if (this.state.submitting) return;
    const prevCode = this.props.order.fieldsToUpdateOrder.promo_code_text;
    this.pushUpdate.cancel();
    this.props.order.setFieldsToUpdateOrder({ promo_code_text: '' });
    this.setState({ error: null });
    runBusy(this, 'promoCancel', () => this.props.order.applyPromoCode())
      .catch(() => {
        this.props.order.setFieldsToUpdateOrder({ promo_code_text: prevCode });
        this.setState({ error: 'Не удалось отменить промокод' });
      });
  };

  applyMaxBonus = () => {
    if (this.state.submitting) return;
    const max = (this.props.order.order || {}).maximum_bonus_spend || 0;
    const prevBonus = this.props.order.fieldsToUpdateOrder.bonus_sum || 0;
    this.props.order.setFieldsToUpdateOrder({ bonus_sum: max });
    this.pushUpdate.cancel();
    this.setState({ error: null });
    runBusy(this, 'bonus', () => this.props.order.updateCommonParams())
      .catch(() => {
        this.props.order.setFieldsToUpdateOrder({ bonus_sum: prevBonus });
        this.setState({ error: 'Не удалось списать бонусы' });
      });
  };

  cancelBonus = () => {
    if (this.state.submitting) return;
    const prevBonus = this.props.order.fieldsToUpdateOrder.bonus_sum || 0;
    this.props.order.setFieldsToUpdateOrder({ bonus_sum: 0 });
    this.pushUpdate.cancel();
    this.setState({ error: null });
    runBusy(this, 'bonus', () => this.props.order.updateCommonParams())
      .catch(() => {
        this.props.order.setFieldsToUpdateOrder({ bonus_sum: prevBonus });
        this.setState({ error: 'Не удалось отменить списание бонусов' });
      });
  };

  // Как в приложении (client_app/.../Cart/OrderForm): выбранная сохранённая
  // карта — просто «Оплатить» (списание с неё); новая карта — с привязкой или
  // без. Выбор «без привязки» — галкой согласия (company.card_save_consent),
  // а без неё, при mobile_card_one_payment, — второй кнопкой.
  //   'make'   — оформить без онлайн-оплаты
  //   'pay'    — ord#pay: сохранённая карта / СБП / новая карта с привязкой
  //   'choice' — две кнопки: с привязкой (ord#pay) и без (ord#just_pay)
  payMode() {
    const order = this.props.order.order || {};
    const fields = this.props.order.fieldsToUpdateOrder;
    if ((order.pay_sum || 0) <= 0 || !isOnlinePayment(order, fields.pay_type_id)) return 'make';
    if (!isCardPayment(order, fields.pay_type_id) || fields.card_id || this.cardConsent()) return 'pay';
    return this.props.auth.companyInfo.mobile_card_one_payment ? 'choice' : 'pay';
  }

  submit = (justPay) => async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (this.state.submitting) return;
    this.setState({ submitting: true, error: null, submittingJustPay: !!justPay });
    try {
      this.pushUpdate.cancel();
      await this.props.order.updateCommonParams(this.props.order.fieldsToUpdateOrder);

      if (this.payMode() !== 'make') {
        const res = await this.props.order.payOrder(!!justPay, this.saveCardParam());
        if (res && res.redirect) {
          window.location.href = res.redirect;
          return;
        }
        // Без redirect успех — только status: 'ok' (payOrder ставит orderMade
        // только в этом случае). Иначе показываем ошибку, а не экран успеха.
        if (!res || res.status !== 'ok') {
          this.setState({ submitting: false, error: (res && res.message) || 'Не удалось оплатить заказ' });
          return;
        }
        this.setState({ submitting: false });
        router.go('#success');
        return;
      }

      await this.props.order.makeOrder();
      this.setState({ submitting: false });
      router.go('#success');
    } catch (err) {
      this.setState({ submitting: false });
      // Гейт подписи отдаёт 400; err.message для него равен буквально 'error'
      // (api/instance берёт body.error || body.message), поэтому разбираем
      // тело явно и уводим клиента на шаг подписи вместо надписи «error».
      if (this.props.order.applyRentActGateError(err)) {
        router.go('#tare_sign');
        return;
      }
      this.setState({ error: err.message || 'Не удалось оформить заказ' });
    }
  };

  // Согласие на сохранение карты (Т-Касса, company.card_save_consent): галка
  // видна только при оплате новой картой. Сняв её, клиент платит без
  // сохранения карты (ord#pay с save_card: false → just_pay).
  cardConsent() {
    const consent = this.props.auth.companyInfo.card_save_consent;
    if (!consent) return null;
    const order = this.props.order.order || {};
    const fields = this.props.order.fieldsToUpdateOrder;
    if ((order.pay_sum || 0) <= 0 || !isCardPayment(order, fields.pay_type_id) || fields.card_id) return null;
    return consent;
  }

  isSaveCardChecked() {
    const consent = this.cardConsent();
    if (!consent) return false;
    return this.state.saveCard === null ? consent.checked_default !== false : this.state.saveCard;
  }

  toggleSaveCard = () => this.setState({ saveCard: !this.isSaveCardChecked() });

  saveCardParam() {
    return this.cardConsent() ? { save_card: this.isSaveCardChecked() } : {};
  }

  renderCardConsent() {
    const consent = this.cardConsent();
    if (!consent) return null;
    return (
      <div className="ng-consents">
        <label className="ng-consent">
          <input
            type="checkbox"
            checked={this.isSaveCardChecked()}
            disabled={this.state.submitting}
            onChange={this.toggleSaveCard}
          />
          <span className="ng-consent__text">
            {consent.text || 'Сохранить карту для следующих оплат'}
            {consent.url && (
              <span>
                {' '}
                <a href={consent.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                  {consent.link_title || 'Условия сохранения карты'}
                </a>
              </span>
            )}
          </span>
        </label>
      </div>
    );
  }

  renderActions() {
    const { submitting, submittingJustPay } = this.state;
    const mode = this.payMode();
    const cls = (extra) => 'btn btn--block' + (extra ? ' ' + extra : '') + (submitting ? ' btn--disabled' : '');

    if (mode === 'choice') {
      return (
        <div className="ng-cart-actions">
          <button type="button" className={cls()} disabled={submitting} onClick={this.submit(false)}>
            {submitting && !submittingJustPay ? 'Отправка…' : 'Оплатить с привязкой карты'}
          </button>
          <button type="button" className={cls('btn--ghost')} disabled={submitting} onClick={this.submit(true)}>
            {submitting && submittingJustPay ? 'Отправка…' : 'Оплатить картой без привязки'}
          </button>
        </div>
      );
    }

    return (
      <div className="ng-cart-actions">
        <button type="button" className={cls()} disabled={submitting} onClick={this.submit(false)}>
          {submitting ? 'Отправка…' : (mode === 'make' ? 'Оформить заказ' : 'Оплатить')}
        </button>
      </div>
    );
  }

  render() {
    const o = this.props.order.order || {};
    const auth = this.props.auth;
    const ci = auth.clientInfo || {};
    const company = auth.companyInfo || {};
    const cur = (company.currency_hash && company.currency_hash.symbol_html) || '';
    const showPromoTrigger = !o.promo_sum && company.is_promocodes;
    // Режим «списание только на 100% заказа»: сервер отдаёт maximum_bonus_spend = 0,
    // когда бонусов не хватает на весь заказ. Блок при этом не прячем — у клиента
    // бонусы есть, и он должен понимать, почему списать их не предлагают.
    const bonusFullOrderOnly = !!o.bonus_spend_full_order_only;
    const canSpendBonus = (o.maximum_bonus_spend || 0) > 0;
    const bonusShort = Math.max((o.sum || 0) - (ci.bonuses || 0), 0);
    const showBonusBlock = !o.bonus_sum && company.is_bonus_avaliable &&
      (canSpendBonus || (bonusFullOrderOnly && (ci.bonuses || 0) > 0));
    const paySum = o.pay_sum || 0;
    const fields = this.props.order.fieldsToUpdateOrder;
    const hasSign = !!((company.cart_coupled || {}).tare_rent_checkout_sign);

    return (
      <div className="ng-payment">
        <div className="ng-checkout__nav">
          <a href="#" onClick={this.goBack}>← Назад к доставке</a>
        </div>

        <h1>Оплата</h1>
        <CheckoutSteps active={hasSign ? 3 : 2} hasSign={hasSign} />

        <div className="ng-payment-summary">
          <div className="ng-payment-summary__total">
            Итого: <strong>{curFormat(paySum)} {cur}</strong>
          </div>
          {o.bonus_sum > 0 && (
            <div className="ng-payment-applied">
              <span>Оплата бонусами: {curFormat(o.bonus_sum)} {cur}</span>
              <button
                type="button"
                className={'ng-payment-applied__cancel' + (isBusy(this, 'bonus') ? ' is-busy' : '')}
                disabled={isBusy(this, 'bonus') || this.state.submitting}
                onClick={this.cancelBonus}
              >
                Отменить
              </button>
            </div>
          )}
          {/* Код «Приведи друга» скидки не даёт (бонусы приходят на баланс),
              поэтому подтверждаем сам факт применения — отменять нечего. */}
          {(o.promo_sum || 0) <= 0 && o.promo_code_text && (
            <div className="ng-payment-applied">
              <span>Промокод «{o.promo_code_text}» принят</span>
            </div>
          )}
          {o.promo_sum > 0 && (
            <div className="ng-payment-applied">
              <span>Промокод {o.promo_code_text ? '«' + o.promo_code_text + '»' : ''} — скидка {curFormat(o.promo_sum)} {cur}</span>
              <button
                type="button"
                className={'ng-payment-applied__cancel' + (isBusy(this, 'promoCancel') ? ' is-busy' : '')}
                disabled={isBusy(this, 'promoCancel') || this.state.submitting}
                onClick={this.cancelPromo}
              >
                Отменить
              </button>
            </div>
          )}
        </div>

        {showPromoTrigger && (
          <button
            type="button"
            className="ng-payment-promo-trigger"
            disabled={this.state.submitting}
            onClick={this.openPromo}
          >
            <PromoIcon />
            <span>У меня есть промокод</span>
          </button>
        )}

        {showBonusBlock && (
          <div className="ng-payment-bonus">
            <div className="ng-payment-bonus__head">
              <span className="ng-payment-bonus__title">Мои бонусы</span>
              <span className="ng-payment-bonus__sum">{curFormat(ci.bonuses || 0)} {cur}</span>
            </div>
            {(ci.bonuses || 0) === 0 ? (
              <p className="ng-payment-bonus__empty">Бонусов для списания пока нет.</p>
            ) : !canSpendBonus ? (
              <p className="ng-payment-bonus__empty">
                Бонусами можно оплатить только весь заказ целиком, не хватает {curFormat(bonusShort)} {cur}.
              </p>
            ) : (
              <button
                type="button"
                className={'btn btn--block' + (isBusy(this, 'bonus') ? ' is-busy' : '')}
                disabled={isBusy(this, 'bonus') || this.state.submitting}
                onClick={this.applyMaxBonus}
              >
                Списать {curFormat(o.maximum_bonus_spend)} {cur}
              </button>
            )}
          </div>
        )}

        {paySum > 0 && <PayType disabled={this.state.submitting} />}

        {this.renderCardConsent()}

        {this.state.error && <div className="order-message">{this.state.error}</div>}

        {this.renderActions()}

        {this.state.promoModalOpen && (
          <div className="dialog">
            <div className="dialog__bg" onClick={this.closePromo} />
            <div className="dialog__content">
              <div className="dialog__close" onClick={this.closePromo} />
              <div className="ng-promo-dialog">
                <h3>Введите промокод</h3>
                <input
                  className="input__text"
                  type="text"
                  placeholder="Промокод"
                  value={this.state.promoInput}
                  onChange={this.setPromoInput}
                  autoFocus
                />
                {this.state.promoError && <div className="order-message">{this.state.promoError}</div>}
                <button
                  type="button"
                  className={'btn btn--block ' + (this.state.applyingPromo ? 'btn--disabled' : '')}
                  disabled={this.state.applyingPromo}
                  onClick={this.applyPromo}
                >
                  {this.state.applyingPromo ? 'Применяем…' : 'Применить'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export const PaymentForm = observer(PaymentFormView, ['orderStore', 'authStore', 'cardStore']);



// WEBPACK FOOTER //
// ./app/javascript/website_newgen/js/components/Cart/Checkout/PaymentForm.jsx